import { memo, useRef } from 'react';
import { Box, Stack, TextField, FormControl, FormHelperText, Checkbox, FormControlLabel, InputAdornment, RadioGroup, Radio } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PeoplePicker } from '@microsoft/mgt-react';
import IconButton from '@mui/material/IconButton';
import { GenericConstant } from '../../../utils/GenericConstant';
import { MtComStyleObj } from '../../../styles/MtComStyleObj';
import { useCommonStyles } from '../../../styles/GenericStyle';
import { usePeoplePickerStyles } from '../../../hooks/usePeoplePickerStyles';
import { numericOnlyKeyDown } from '../../../utils/HelperFunction';

const resolveList = (field: any, listValues: any): any[] =>
    Array.isArray(field?.list) ? field.list : (listValues?.[field?.list] ?? []);

const withMultiValueHint = (placeholder: string) => {
    const hint = 'Use comma for multiple values';
    if (!placeholder?.trim()) return hint;
    return placeholder.toLowerCase().includes('comma') ? placeholder : `${placeholder} (${hint})`;
};

interface FilterFieldRendererProps {
    field: any;
    formData: Record<string, any>;
    formAllCheckBox: Record<string, any>;
    listValues: any;
    dateValidMsg: string;
    peoplePickerInvoke: string;
    handleChange: (name: string, value: any, field: any) => void;

    isChecked: (value: string | number, field: any) => boolean;
    handleSelectionChanged: (e: Event, fieldName: string) => void;
}

function FilterFieldRenderer({
    field,
    formData,
    formAllCheckBox,
    listValues,
    dateValidMsg,
    peoplePickerInvoke,
    handleChange,
    isChecked,
    handleSelectionChanged,
}: FilterFieldRendererProps) {
    const classes = useCommonStyles();
    const peoplePickerRef = useRef<HTMLDivElement>(null);
    usePeoplePickerStyles(peoplePickerRef, true, peoplePickerInvoke);

    const keyDownEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const target = event?.target as HTMLInputElement;
        if (target?.type == "number") {
            numericOnlyKeyDown(event);
        }
    };

    switch (field.type) {
        case 'textMulti':
            return (
                <FormControl sx={{ width: { xs: '100%', sm: '100%', md: '100%', p: 0 } }} fullWidth>
                    <TextField
                        placeholder={withMultiValueHint(field?.placeholder || `Enter ${field?.label}`)}
                        multiline
                        value={formData[field?.name] || ''}
                        maxRows={3}
                        minRows={1}
                        sx={(theme) => ({
                            ...MtComStyleObj(theme).textFocusHoverStyle,
                            ...MtComStyleObj(theme).textBoxValueStyle,
                            ...MtComStyleObj(theme).placeHolderStyle,
                            '& .MuiInputBase-root': { py: '8.5px' },
                        })}
                        slotProps={{
                            input: { sx: { minHeight: 30 } },
                            formHelperText: { sx: { ml: 0 } },
                        }}
                        onChange={(e) => handleChange(field.name, e.target.value, field)}
                    />
                </FormControl>
            );

        case 'text':
        case 'number':
            return (
                <FormControl sx={{ width: { xs: '100%', sm: '100%', md: '100%' } }} fullWidth>
                    <TextField
                        placeholder={field.type === 'text'
                            ? withMultiValueHint(field.placeholder || `Enter ${field.label}`)
                            : (field.placeholder || `Enter ${field.label}`)}
                        variant="outlined"
                        value={formData[field.name] ?? ''}
                        type={field.type === 'number' ? 'number' : 'text'}
                        onKeyDown={keyDownEvent}
                        sx={(theme) => ({
                            ...MtComStyleObj(theme).textFocusHoverStyle,
                            ...MtComStyleObj(theme).textBoxValueStyle,
                            ...MtComStyleObj(theme).placeHolderStyle,
                        })}
                        slotProps={{
                            input: {
                                sx: { height: 40 },
                                ...(field.type === 'number' && { inputProps: { min: field.min ?? 0, max: field.max, step: field.step ?? 1 } }),
                            },
                            formHelperText: { sx: { ml: 0 } },
                        }}
                        onChange={(e) => {
                            if (field.type === 'number') {
                                const val = e.target.value;
                                if (val === '') { handleChange(field.name, '', field); return; }
                                const num = Number(val);
                                if (!isNaN(num) && num >= (field.min ?? 0) && (field.max == null || num <= field.max)) handleChange(field.name, val, field);
                            } else {
                                handleChange(field.name, e.target.value, field);
                            }
                        }}
                    />
                </FormControl>
            );

        case 'PeoplePicker':
            return (
                <FormControl sx={{ width: { xs: '100%', sm: '100%', md: '100%' } }} fullWidth>
                    <Box ref={peoplePickerRef}>
                        <PeoplePicker
                            id="filter_uname"
                            className="mgt-people-picker"
                            placeholder="Enter Username"
                            selectedPeople={formData[field.name] ?? []}
                            type="person"
                            userType="user"
                            allowAnyEmail={true}
                            selectionMode="multiple"
                            groupId={GenericConstant.PEOPLEPICKGROUPID}
                            selectionChanged={(e: Event) => handleSelectionChanged(e, field?.name)}
                        />
                    </Box>
                </FormControl>
            );

        case 'select':
            return (
                <Stack direction="column" sx={{ width: '100%', displayalignItems: 'stretch' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                disableRipple
                                checked={formAllCheckBox[field?.name + '_all']}
                                sx={GenericConstant.CHECKBOXSTYLE}
                                checkedIcon={<Box component="img" alt="" src="icons/CheckBox.svg" />}
                                onChange={(e) => handleChange(field.name + '_all', e, field)}
                                size="small"
                            />
                        }
                        sx={{ height: 30 }}
                        slotProps={{ typography: { className: classes.labelNoto14Bold400 } }}
                        label="Select All"
                    />
                    {resolveList(field, listValues)?.map((option: any) => (
                        <FormControlLabel
                            key={option[field?.listKey]}
                            control={
                                <Checkbox
                                    disableRipple
                                    value={option[field?.listKey]}
                                    checked={isChecked(option[field?.listKey], field)}
                                    sx={GenericConstant.CHECKBOXSTYLE}
                                    checkedIcon={<Box component="img" alt="" src="icons/CheckBox.svg" />}
                                    onChange={(e) => handleChange(field?.name, e, field)}
                                    size="small"
                                />
                            }
                            sx={{ height: 30 }}
                            slotProps={{ typography: { className: classes.labelNoto14Bold400 } }}
                            label={option[field?.listValue]}
                        />
                    ))}
                </Stack>
            );

        case 'dateRange':
            return (
                <FormControl sx={{ width: { xs: '40%', sm: '100%', md: '100%' } }} size="small">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateRangePicker
                            slots={{ field: SingleInputDateRangeField }}
                            format="YYYY/MM/DD"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    placeholder: 'Select Date range',
                                    InputProps: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="Open end date picker"
                                                    edge="end"
                                                    size="small"
                                                    disableRipple
                                                    disableFocusRipple
                                                    sx={{
                                                        '&:focus': { outline: 'none' },
                                                        '&.Mui-focusVisible, &:focus-visible': { outline: 'none', boxShadow: 'none' },
                                                    }}
                                                >
                                                    <img src="icons/calendar.svg" alt="Information" style={{ width: 16, height: 16 }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                },
                            }}
                            value={formData[field?.name] || [null, null]}
                            sx={(theme) => ({
                                ...MtComStyleObj(theme).textFocusHoverStyle,
                                ...MtComStyleObj(theme).textBoxValueStyle,
                                ...MtComStyleObj(theme).placeHolderStyle,
                            })}
                            onChange={(newValue) => handleChange(field?.name, newValue, field)}
                        />
                    </LocalizationProvider>
                    <FormHelperText sx={{ ml: 0, mt: 0 }} className={classes.errorText}>
                        {dateValidMsg}
                    </FormHelperText>
                </FormControl>
            );

        case 'radio':
            return (
                <FormControl sx={{ width: '100%' }}>
                    <RadioGroup
                        value={formData[field?.name] !== undefined && formData[field?.name] !== null ? String(formData[field?.name]) : ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'null') {
                                handleChange(field?.name, null, field);
                            } else if (value === 'true') {
                                handleChange(field?.name, true, field);
                            } else if (value === 'false') {
                                handleChange(field?.name, false, field);
                            }
                        }}
                    >
                        {resolveList(field, listValues)?.map((option: any) => (
                            <FormControlLabel
                                key={String(option[field?.listKey])}
                                value={String(option[field?.listKey])}
                                control={<Radio size="small" sx={GenericConstant.CHECKBOXSTYLE} />}
                                label={option[field?.listValue]}
                                slotProps={{ typography: { className: classes.labelNoto14Bold400 } }}
                                sx={{ height: 30 }}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );

        default:
            return null;
    }
}

export default memo(FilterFieldRenderer);
